import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { cn } from '../../lib/cn';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { callCommit } from '../../services/assistant/cimeToolsClient';
import {
  FEE_AGREEMENT_MODE_LABELS,
  type FeeAgreementMode,
  type FeeService,
} from '../../services/billing/feeAgreementService';
import type { ExtractedAgreement } from '../../lib/assistant/mapExtraction';

interface Props {
  extracted: ExtractedAgreement;
  warnings: string[];
  dossierId?: string;
  onCreated: (id: string) => void;
  onError: (msg: string) => void;
}

const MODES: FeeAgreementMode[] = ['forfait', 'hourly', 'mixed'];

function WarningBadge() {
  return (
    <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 ml-1.5">
      à vérifier
    </span>
  );
}

function FieldLabel({
  label,
  fieldKey,
  warnings,
  children,
}: {
  label: string;
  fieldKey: string;
  warnings: string[];
  children: React.ReactNode;
}) {
  const hasWarning = warnings.some((w) =>
    w.toLowerCase().includes(fieldKey.toLowerCase()),
  );
  return (
    <div>
      <label className="flex items-center text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1.5">
        {label}
        {hasWarning && <WarningBadge />}
      </label>
      {children}
    </div>
  );
}

export function FeeAgreementPreviewCard({
  extracted,
  warnings,
  dossierId: initialDossierId,
  onCreated,
  onError,
}: Props) {
  const canCreate = useCanAccessFeature('fee_agreement_ai_create');
  const { data: dossiers, isLoading: loadingDossiers } = useDossiers();

  const [mode, setMode] = useState<FeeAgreementMode>(extracted.billing_mode);
  const [forfait, setForfait] = useState(extracted.forfait_amount?.toString() ?? '');
  const [hourly, setHourly] = useState(extracted.hourly_rate?.toString() ?? '');
  const [services, setServices] = useState<FeeService[]>(extracted.services);
  const [newService, setNewService] = useState('');
  const [successRate, setSuccessRate] = useState(extracted.success_fee_rate?.toString() ?? '');
  const [successBasis, setSuccessBasis] = useState(extracted.success_fee_basis ?? '');
  const [successNotes, setSuccessNotes] = useState(extracted.success_fee_notes ?? '');
  const [signedAt, setSignedAt] = useState(extracted.signed_at ?? '');
  const [notes, setNotes] = useState(extracted.notes ?? '');
  const [selectedDossierId, setSelectedDossierId] = useState(initialDossierId ?? '');
  const [loading, setLoading] = useState(false);

  const showForfait = mode === 'forfait' || mode === 'mixed';
  const showHourly = mode === 'hourly' || mode === 'mixed';

  function addService() {
    const label = newService.trim();
    if (!label) return;
    setServices((s) => [...s, { label }]);
    setNewService('');
  }

  async function handleCreate() {
    if (!selectedDossierId) {
      onError("Sélectionnez un dossier avant de créer la convention.");
      return;
    }
    const agreement: ExtractedAgreement = {
      billing_mode: mode,
      forfait_amount: showForfait && forfait ? Number(forfait) : null,
      hourly_rate: showHourly && hourly ? Number(hourly) : null,
      services,
      success_fee_rate: successRate ? Number(successRate) : null,
      success_fee_basis: successBasis.trim() || null,
      success_fee_notes: successNotes.trim() || null,
      signed_at: signedAt || null,
      notes: notes.trim() || null,
    };
    setLoading(true);
    try {
      const { id } = await callCommit(selectedDossierId, agreement);
      onCreated(id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === 'access-denied') {
        onError("Acces refuse : cle invalide ou expiree.");
      } else if (msg.startsWith('permission:')) {
        onError(msg.replace('permission:', '').trim());
      } else {
        onError(msg || "Erreur lors de la creation de la convention.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-cime-text-primary">
            Convention extraite
          </p>
          {warnings.length > 0 && (
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <span aria-hidden="true">⚠️</span>
              {warnings.length} champ{warnings.length > 1 ? 's' : ''} à vérifier
            </span>
          )}
        </div>

        {/* Mode */}
        <FieldLabel label="Mode de facturation" fieldKey="billing_mode" warnings={warnings}>
          <div className="grid grid-cols-3 gap-1.5 max-w-xs">
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'px-2 py-1.5 text-xs rounded-md border transition',
                  mode === m
                    ? 'bg-cime-primary text-white border-cime-primary'
                    : 'bg-white border-neutral-200 hover:border-cime-primary/40',
                )}
              >
                {FEE_AGREEMENT_MODE_LABELS[m]}
              </button>
            ))}
          </div>
        </FieldLabel>

        {/* Montants */}
        <div className="grid grid-cols-2 gap-3">
          {showForfait && (
            <FieldLabel label="Forfait (EUR)" fieldKey="forfait_amount" warnings={warnings}>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={forfait}
                onChange={(e) => setForfait(e.target.value)}
                placeholder="3000"
              />
            </FieldLabel>
          )}
          {showHourly && (
            <FieldLabel label="Taux horaire (EUR/h)" fieldKey="hourly_rate" warnings={warnings}>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={hourly}
                onChange={(e) => setHourly(e.target.value)}
                placeholder="250"
              />
            </FieldLabel>
          )}
        </div>

        {/* Prestations */}
        <FieldLabel label="Prestations couvertes" fieldKey="services" warnings={warnings}>
          <div className="space-y-1.5">
            {services.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex-1 text-sm text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md px-2.5 py-1.5">
                  {s.label}
                </span>
                <button
                  type="button"
                  onClick={() => setServices((arr) => arr.filter((_, j) => j !== i))}
                  className="text-neutral-400 hover:text-red-600"
                  aria-label="Retirer la prestation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Input
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addService();
                  }
                }}
                placeholder="Ajouter une prestation…"
              />
              <Button type="button" variant="ghost" onClick={addService}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </FieldLabel>

        {/* Honoraires de résultat */}
        <div className="border-t border-neutral-100 pt-4">
          <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2">
            Honoraires de resultat
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FieldLabel label="Taux (%)" fieldKey="success_fee_rate" warnings={warnings}>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={successRate}
                onChange={(e) => setSuccessRate(e.target.value)}
                placeholder="10"
              />
            </FieldLabel>
            <FieldLabel label="Base" fieldKey="success_fee_basis" warnings={warnings}>
              <Input
                value={successBasis}
                onChange={(e) => setSuccessBasis(e.target.value)}
                placeholder="sur les sommes obtenues"
              />
            </FieldLabel>
          </div>
          <div className="mt-3">
            <FieldLabel label="Conditions (optionnel)" fieldKey="success_fee_notes" warnings={warnings}>
              <textarea
                value={successNotes}
                onChange={(e) => setSuccessNotes(e.target.value)}
                rows={2}
                className="w-full text-sm rounded-cime border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cime-primary/30"
                placeholder="En cas de decision favorable…"
              />
            </FieldLabel>
          </div>
        </div>

        {/* Date de signature + notes */}
        <div className="border-t border-neutral-100 pt-4 space-y-3">
          <FieldLabel label="Date de signature" fieldKey="signed_at" warnings={warnings}>
            <Input
              type="date"
              value={signedAt}
              onChange={(e) => setSignedAt(e.target.value)}
            />
          </FieldLabel>
          <FieldLabel label="Notes (optionnel)" fieldKey="notes" warnings={warnings}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full text-sm rounded-cime border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cime-primary/30"
            />
          </FieldLabel>
        </div>

        {/* Sélecteur de dossier */}
        <div className="border-t border-neutral-100 pt-4">
          <label className="block text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1.5">
            Dossier
          </label>
          {loadingDossiers ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Spinner className="w-4 h-4" /> Chargement…
            </div>
          ) : (
            <select
              value={selectedDossierId}
              onChange={(e) => setSelectedDossierId(e.target.value)}
              className="w-full text-sm rounded-cime border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cime-primary/30 bg-white"
            >
              <option value="">-- Sélectionnez un dossier --</option>
              {(dossiers ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Action */}
        {canCreate ? (
          <Button
            onClick={handleCreate}
            disabled={loading || !selectedDossierId}
            className="w-full"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner className="w-4 h-4" /> Creation…
              </span>
            ) : (
              "Creer la convention"
            )}
          </Button>
        ) : (
          <p className="text-sm text-cime-text-muted text-center py-2">
            Vous n'avez pas la permission de creer une convention via Nova.
          </p>
        )}
      </div>
    </Card>
  );
}
